// Packages
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
} from "@stencil/core";

/**
 * `fx-segment-button` is a headless tab button used inside `fx-segment`.
 * It exposes its selected/disabled state via `data-*` and ARIA attributes and
 * notifies the parent segment when activated by click, Enter, or Space.
 *
 * @slot - The button label.
 *
 * @part base - The visible native `<button>` element.
 * @part label - The wrapper around the default slot.
 * @part indicator - The visual selection indicator.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-segment-button",
  shadow: true,
})
export class FxSegmentButton {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  private baseEl?: HTMLButtonElement;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - If `true`, the button is disabled and cannot be activated.
   */
  @Prop()
  disabled: boolean = false;

  /**
   * @prop - The `id` of the `fx-segment-content` this button activates.
   */
  @Prop()
  contentId?: string;

  /**
   * @prop - If `true`, the button is the currently selected tab. Controlled by
   * the parent `fx-segment`.
   */
  @Prop({ mutable: true })
  selected: boolean = false;

  /**
   * @prop - The `tabindex` applied to the internal `<button>`. Managed by the
   * parent `fx-segment` for roving `tabindex` keyboard navigation.
   */
  @Prop({ mutable: true })
  segmentTabIndex: number = 0;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the button is activated by click or keyboard.
   */
  @Event()
  fxSegmentButtonActivate!: EventEmitter<{ contentId: string | undefined }>;

  /**
   * @event - Emitted for arrow, Home, and End keys so the parent `fx-segment`
   * can manage roving focus.
   */
  @Event()
  fxSegmentButtonKeydown!: EventEmitter<{ key: string; contentId: string | undefined }>;

  // --------------------------------------------
  // Methods
  // --------------------------------------------

  /**
   * @method - Moves focus to the internal button element.
   */
  @Method()
  async setFocus(): Promise<void> {
    this.baseEl?.focus();
  }

  // --------------------------------------------
  // Private handlers
  // --------------------------------------------

  private handleClick = (ev: MouseEvent) => {
    if (this.disabled) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
    this.activate();
  };

  private handleKeyDown = (ev: KeyboardEvent) => {
    if (this.disabled) {
      return;
    }

    const handledKeys = ["ArrowRight", "ArrowLeft", "Home", "End", " ", "Enter", "Spacebar"];
    if (!handledKeys.includes(ev.key)) {
      return;
    }

    ev.preventDefault();

    if (ev.key === " " || ev.key === "Enter" || ev.key === "Spacebar") {
      this.activate();
      return;
    }

    this.fxSegmentButtonKeydown.emit({ key: ev.key, contentId: this.contentId });
  };

  private activate() {
    if (this.disabled) {
      return;
    }
    this.fxSegmentButtonActivate.emit({ contentId: this.contentId });
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const { disabled, selected, contentId, segmentTabIndex } = this;

    return (
      <Host
        data-selected={selected ? "true" : "false"}
        data-disabled={disabled ? "true" : "false"}
        data-content-id={contentId}
      >
        <button
          part="base"
          role="tab"
          type="button"
          ref={(el) => (this.baseEl = el as HTMLButtonElement)}
          disabled={disabled}
          aria-disabled={disabled ? "true" : null}
          aria-selected={selected ? "true" : "false"}
          aria-controls={contentId || undefined}
          tabIndex={disabled ? -1 : segmentTabIndex}
          onClick={this.handleClick}
          onKeyDown={this.handleKeyDown}
        >
          <span part="label">
            <slot />
          </span>
          <span part="indicator" aria-hidden="true" />
        </button>
      </Host>
    );
  }
}
